import React, { useState, useEffect } from 'react';
import { fromIso, toIso } from '../utils/businessDate';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getTenantToday } from '../utils/dateUtils';

import { DateField } from './shared/DateField';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 2rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 1rem;
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(30, 41, 59, 0.8);
  color: white;
  font-size: 0.875rem;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
  
  option {
    background: #1e293b;
    color: white;
  }
`;


const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Card = styled.div`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MetricLabel = styled.span`
  color: #94a3b8;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.span`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: white;
  
  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  th {
    color: #94a3b8;
    font-weight: 600;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  td {
    font-size: 0.875rem;
  }
  
  tr:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 1rem;
  color: #fca5a5;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  padding: 1rem;
  color: #86efac;
  margin-bottom: 1rem;
`;

const Loading = styled.div`
  text-align: center;
  color: #94a3b8;
  padding: 2rem;
`;

interface NavData {
  id: number;
  portfolioId: number;
  portfolioName: string;
  navDate: string;
  totalAssets: number;
  totalLiabilities: number;
  netAssetValue: number;
  sharesOutstanding?: number;
  navPerShare?: number;
  assets: {
    securitiesMarketValue: number;
    cashAndEquivalents: number;
    receivables: number;
    otherAssets: number;
  };
  liabilities: {
    payables: number;
    accruedFees: number;
    otherLiabilities: number;
  };
  fees: {
    managementFee: number;
    performanceFee: number;
    totalFees: number;
  };
  performance?: {
    previousNav?: number;
    navChange?: number;
    navChangePercent?: number;
  };
  calculationStatus: string;
}

const NavReportingScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const [navDate, setNavDate] = useState<string>(getTenantToday());
  const [navData, setNavData] = useState<NavData | null>(null);
  const [navHistory, setNavHistory] = useState<NavData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load portfolios
  useEffect(() => {
    loadPortfolios();
  }, []);

  const loadPortfolios = async () => {
    console.log('Fetching portfolios...');
    try {
      const response = await axios.get('/api/dimensions/portfolios');
      console.log('Portfolios response:', response.data);
      // Handle both Page object and direct array response
      const data = response.data?.content || response.data || [];
      if (!Array.isArray(data)) {
        console.error('Invalid portfolios data format:', response.data);
        return;
      }
      
      console.log('Total portfolios found:', data.length);
      
      // Filter to only funds
      const funds = data.filter((p: any) => {
        // Log each portfolio to see its structure
        console.log('Checking portfolio:', p.portfolioName, 'isFund:', p.isFund, 'type:', p.portfolioType);
        
        const isMatch = p.isFund === true || p.isFund === 'true' || 
          (p.portfolioType && ['MUTUAL_FUND', 'HEDGE_FUND', 'PRIVATE_FUND', 'ETF', 'CLOSED_END_FUND'].includes(p.portfolioType));
        
        if (isMatch) console.log('Matches fund criteria:', p.portfolioName);
        return isMatch;
      });
      
      // FALLBACK: If no funds found after filtering, show ALL portfolios so the dropdown isn't blank
      const finalPortfolios = funds.length > 0 ? funds : data;
      if (funds.length === 0 && data.length > 0) {
        console.log('No funds found after filtering, falling back to all portfolios');
      }
      
      setPortfolios(finalPortfolios);
      if (finalPortfolios.length > 0 && !selectedPortfolioId) {
        setSelectedPortfolioId(finalPortfolios[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load portfolios:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait a moment before trying again.');
      } else {
        setError(err.response?.data?.message || 'Failed to load portfolios. Please check your connection.');
      }
    }
  };

  const calculateNav = async () => {
    if (!selectedPortfolioId) {
      setError('Please select a portfolio');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`/api/nav/calculate?portfolioId=${selectedPortfolioId}&navDate=${navDate}`, {});

      if (response.data.success) {
        setNavData(response.data.nav);
        setSuccess('NAV calculated successfully');
        loadNavHistory();
      } else {
        setError(response.data.error || 'Failed to calculate NAV');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to calculate NAV');
    } finally {
      setLoading(false);
    }
  };

  const loadNav = async () => {
    if (!selectedPortfolioId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/nav/${selectedPortfolioId}?navDate=${navDate}`);

      if (response.data.success) {
        setNavData(response.data.nav);
      } else {
        setError(response.data.error || 'NAV not found');
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('NAV not found for this date. Click "Calculate NAV" to create it.');
      } else {
        setError(err.response?.data?.error || 'Failed to load NAV');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadNavHistory = async () => {
    if (!selectedPortfolioId) {
      return;
    }

    try {
      const startDate = fromIso(getTenantToday());
      startDate.setDate(startDate.getDate() - 30);
      
      const response = await axios.get(
        `/api/nav/${selectedPortfolioId}/history?startDate=${toIso(startDate)}&endDate=${navDate}`
      );

      if (response.data.success) {
        setNavHistory(response.data.navHistory || []);
      }
    } catch (err: any) {
      console.error('Failed to load NAV history:', err);
    }
  };

  const finalizeNav = async () => {
    if (!selectedPortfolioId) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`/api/nav/${selectedPortfolioId}/finalize?navDate=${navDate}`, {});

      if (response.data.success) {
        setSuccess('NAV finalized successfully');
        loadNav();
      } else {
        setError(response.data.error || 'Failed to finalize NAV');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to finalize NAV');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPortfolioId) {
      loadNav();
      loadNavHistory();
    }
  }, [selectedPortfolioId, navDate]);

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return 'N/A';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <Container>
      <Header>
        <Title>📊 NAV Reporting</Title>
        <Subtitle>Net Asset Value calculation and reporting for fund portfolios</Subtitle>
      </Header>

      <Controls>
        <Select
          value={selectedPortfolioId || ''}
          onChange={(e) => setSelectedPortfolioId(Number(e.target.value))}
        >
          <option value="">Select Portfolio...</option>
          {portfolios.map(p => (
            <option key={p.id} value={p.id}>{p.portfolioName}</option>
          ))}
        </Select>

        <DateField
          value={navDate ?? ''}
          onChange={iso => setNavDate(iso)}
        />

        <Button onClick={calculateNav} disabled={loading || !selectedPortfolioId}>
          {loading ? 'Calculating...' : 'Calculate NAV'}
        </Button>

        {navData && navData.calculationStatus === 'CALCULATED' && (
          <Button onClick={finalizeNav} disabled={loading}>
            Finalize NAV
          </Button>
        )}
      </Controls>

      {error && (
        <div className="flex flex-col gap-4">
          <ErrorMessage>{error}</ErrorMessage>
          {error.includes('Rate limit') && (
            <Button onClick={() => loadPortfolios()} style={{ width: 'fit-content' }}>
              Retry Now
            </Button>
          )}
        </div>
      )}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {loading && !navData && <Loading>Loading NAV data...</Loading>}

      {navData && (
        <>
          <Card>
            <CardTitle>NAV Summary</CardTitle>
            <Grid>
              <Metric>
                <MetricLabel>Net Asset Value</MetricLabel>
                <MetricValue>{formatCurrency(navData.netAssetValue)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>NAV per Share</MetricLabel>
                <MetricValue>{formatCurrency(navData.navPerShare)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Total Assets</MetricLabel>
                <MetricValue>{formatCurrency(navData.totalAssets)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Total Liabilities</MetricLabel>
                <MetricValue>{formatCurrency(navData.totalLiabilities)}</MetricValue>
              </Metric>
              {navData.performance?.navChangePercent !== undefined && (
                <Metric>
                  <MetricLabel>Daily Change</MetricLabel>
                  <MetricValue style={{ 
                    color: (navData.performance.navChangePercent || 0) >= 0 ? '#10b981' : '#ef4444' 
                  }}>
                    {formatPercent(navData.performance.navChangePercent)}
                  </MetricValue>
                </Metric>
              )}
            </Grid>
          </Card>

          <Card>
            <CardTitle>Asset Breakdown</CardTitle>
            <Grid>
              <Metric>
                <MetricLabel>Securities</MetricLabel>
                <MetricValue>{formatCurrency(navData.assets.securitiesMarketValue)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Cash & Equivalents</MetricLabel>
                <MetricValue>{formatCurrency(navData.assets.cashAndEquivalents)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Receivables</MetricLabel>
                <MetricValue>{formatCurrency(navData.assets.receivables)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Other Assets</MetricLabel>
                <MetricValue>{formatCurrency(navData.assets.otherAssets)}</MetricValue>
              </Metric>
            </Grid>
          </Card>

          <Card>
            <CardTitle>Liability Breakdown</CardTitle>
            <Grid>
              <Metric>
                <MetricLabel>Payables</MetricLabel>
                <MetricValue>{formatCurrency(navData.liabilities.payables)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Accrued Fees</MetricLabel>
                <MetricValue>{formatCurrency(navData.liabilities.accruedFees)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Other Liabilities</MetricLabel>
                <MetricValue>{formatCurrency(navData.liabilities.otherLiabilities)}</MetricValue>
              </Metric>
            </Grid>
          </Card>

          <Card>
            <CardTitle>Fee Accruals</CardTitle>
            <Grid>
              <Metric>
                <MetricLabel>Management Fee</MetricLabel>
                <MetricValue>{formatCurrency(navData.fees.managementFee)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Performance Fee</MetricLabel>
                <MetricValue>{formatCurrency(navData.fees.performanceFee)}</MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Total Fees</MetricLabel>
                <MetricValue>{formatCurrency(navData.fees.totalFees)}</MetricValue>
              </Metric>
            </Grid>
          </Card>

          {navHistory.length > 0 && (
            <Card>
              <CardTitle>NAV History (Last 30 Days)</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>NAV</th>
                    <th>NAV per Share</th>
                    <th>Change</th>
                    <th>Change %</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {navHistory.map((nav) => (
                    <tr key={nav.id}>
                      <td>{new Date(nav.navDate).toLocaleDateString()}</td>
                      <td>{formatCurrency(nav.netAssetValue)}</td>
                      <td>{formatCurrency(nav.navPerShare)}</td>
                      <td style={{ 
                        color: (nav.performance?.navChange || 0) >= 0 ? '#10b981' : '#ef4444' 
                      }}>
                        {formatCurrency(nav.performance?.navChange)}
                      </td>
                      <td style={{ 
                        color: (nav.performance?.navChangePercent || 0) >= 0 ? '#10b981' : '#ef4444' 
                      }}>
                        {formatPercent(nav.performance?.navChangePercent)}
                      </td>
                      <td>{nav.calculationStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </>
      )}

      {!navData && !loading && selectedPortfolioId && (
        <Card>
          <p style={{ color: '#94a3b8', textAlign: 'center' }}>
            No NAV data found. Click "Calculate NAV" to create a NAV calculation.
          </p>
        </Card>
      )}
    </Container>
  );
};

export default NavReportingScreen;
